import {
	type DateValue,
	getLocalTimeZone,
	parseDate,
	today,
} from "@internationalized/date";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { OptionDisplayField } from "@/features/questions/components";
import {
	DateRangeType,
	QuestionOptionDisplayType,
	type QuestionSettings,
	QuestionType,
} from "@/types/question";

type QuestionOption = {
	id: string;
	text: string;
	icon?: React.ComponentType<{ className?: string }>;
};

type QuestionPreviewProps = {
	type: QuestionType | 999;
	options?: QuestionOption[];
	displayType?: QuestionOptionDisplayType;
	settings?: QuestionSettings;
	title?: string;
	isRequired?: boolean;
};

function toPreviewFieldValue(
	isCheckbox: boolean,
	selectedValue: string | string[] | undefined
): string | string[] | undefined {
	if (!isCheckbox) {
		return selectedValue;
	}
	if (Array.isArray(selectedValue)) {
		return selectedValue;
	}
	if (selectedValue) {
		return [selectedValue];
	}
	return [];
}

function IconOptionsPreview({
	options,
	selectedIds,
	onSelect,
}: {
	options: QuestionOption[];
	selectedIds: Set<string>;
	onSelect: (optionId: string) => void;
}) {
	return (
		<div className="flex gap-4">
			{options.map((option) => {
				const IconComponent = option.icon;
				const isSelected = selectedIds.has(option.id);
				return (
					<button
						className={`flex cursor-pointer flex-col items-center gap-2 transition-all ${
							isSelected ? "scale-105" : ""
						}`}
						key={option.id}
						onClick={() => onSelect(option.id)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onSelect(option.id);
							}
						}}
						type="button"
					>
						{IconComponent ? (
							<div
								className={`rounded-lg border-2 p-4 transition-colors ${
									isSelected
										? "border-blue-500 bg-blue-50"
										: "border-gray-300 bg-white hover:border-gray-400"
								}`}
							>
								<IconComponent
									className={`h-8 w-8 ${
										isSelected ? "text-blue-600" : "text-gray-600"
									}`}
								/>
							</div>
						) : null}
						<span
							className={`text-xs ${
								isSelected ? "font-semibold text-blue-700" : "text-gray-700"
							}`}
						>
							{option.text}
						</span>
					</button>
				);
			})}
		</div>
	);
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: preview covers all question types in one component
export function QuestionPreview({
	type,
	options = [],
	displayType,
	settings,
	title,
	isRequired = false,
}: QuestionPreviewProps) {
	const [selectedValue, setSelectedValue] = useState<
		string | string[] | undefined
	>(undefined);
	const [isAccepted, setIsAccepted] = useState(false);
	const [previewDate, setPreviewDate] = useState<DateValue | null>(null);
	const todayDate = today(getLocalTimeZone());

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset when preview config changes
	useEffect(() => {
		setSelectedValue(undefined);
		setIsAccepted(false);
	}, [displayType, type, title, settings?.isMultipleSelection]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset when date settings change
	useEffect(() => {
		setPreviewDate(null);
	}, [settings?.dateRangeType, settings?.minDate, settings?.maxDate]);

	const dateRestrictions = useMemo(() => {
		if (!settings?.dateRangeType) {
			return {
				minValue: undefined,
				maxValue: undefined,
				isDateUnavailable: undefined,
			};
		}

		const dateRangeType = settings.dateRangeType as DateRangeType;

		switch (dateRangeType) {
			case DateRangeType.PastOnly:
				return {
					minValue: undefined,
					maxValue: todayDate,
					isDateUnavailable: (date: DateValue) => date.compare(todayDate) > 0,
				};

			case DateRangeType.FutureOnly:
				return {
					minValue: todayDate,
					maxValue: undefined,
					isDateUnavailable: (date: DateValue) => date.compare(todayDate) < 0,
				};

			case DateRangeType.Range: {
				const minDate = settings.minDate
					? parseDate(settings.minDate.toISOString().split("T")[0])
					: undefined;
				const maxDate = settings.maxDate
					? parseDate(settings.maxDate.toISOString().split("T")[0])
					: undefined;
				return {
					minValue: minDate,
					maxValue: maxDate,
					isDateUnavailable: (date: DateValue) => {
						if (minDate && date.compare(minDate) < 0) {
							return true;
						}
						if (maxDate && date.compare(maxDate) > 0) {
							return true;
						}
						return false;
					},
				};
			}

			default:
				return {
					minValue: undefined,
					maxValue: undefined,
					isDateUnavailable: undefined,
				};
		}
	}, [
		settings?.dateRangeType,
		settings?.minDate,
		settings?.maxDate,
		todayDate,
	]);

	const handleIconSelect = (optionId: string) => {
		if (type === 999) {
			setSelectedValue(optionId);
			return;
		}
		setSelectedValue((prev) => {
			if (Array.isArray(prev)) {
				if (prev.includes(optionId)) {
					return prev.filter((id) => id !== optionId);
				}
				return [...prev, optionId];
			}
			if (prev === optionId) {
				return;
			}
			return optionId;
		});
	};

	const renderBooleanAcceptance = () => (
		<Checkbox
			isSelected={isAccepted}
			label={
				<span className="inline-flex items-start gap-1 text-gray-700 text-sm">
					<span>{title?.trim() || "Título da pergunta"}</span>
					{isRequired ? (
						<span className="shrink-0 text-brand-tertiary">*</span>
					) : null}
				</span>
			}
			onChange={setIsAccepted}
		/>
	);

	if (type === QuestionType.Boolean) {
		return renderBooleanAcceptance();
	}

	if (type === QuestionType.Text) {
		return (
			<Input className="bg-gray-50" isDisabled placeholder="Campo de texto" />
		);
	}

	if (type === QuestionType.Number) {
		return (
			<Input
				className="bg-gray-50"
				isDisabled
				placeholder="Campo de número"
				type="number"
			/>
		);
	}

	if (type === QuestionType.Time) {
		return (
			<Input
				className="bg-gray-50"
				isDisabled
				placeholder="Seleção de hora"
				type="time"
			/>
		);
	}

	if (type === QuestionType.Date) {
		if (settings?.dateRangeType === DateRangeType.Range) {
			const minDate = settings.minDate
				? parseDate(settings.minDate.toISOString().split("T")[0])
				: undefined;
			const maxDate = settings.maxDate
				? parseDate(settings.maxDate.toISOString().split("T")[0])
				: undefined;

			if (minDate && maxDate && minDate.compare(maxDate) > 0) {
				return (
					<div className="rounded-lg border border-red-200 bg-red-50 p-4">
						<p className="text-red-700 text-sm">
							Configuração inválida: dados mínimos maiores que dados máximo
						</p>
					</div>
				);
			}

			return (
				<DateRangePicker
					isDateUnavailable={dateRestrictions.isDateUnavailable}
					maxValue={dateRestrictions.maxValue}
					minValue={dateRestrictions.minValue}
				/>
			);
		}

		return (
			<DatePicker
				isDateUnavailable={dateRestrictions.isDateUnavailable}
				maxValue={dateRestrictions.maxValue}
				minValue={dateRestrictions.minValue}
				onChange={(value) => {
					if (!value) {
						setPreviewDate(null);
						return;
					}

					if (dateRestrictions.isDateUnavailable?.(value)) {
						return;
					}

					if (
						dateRestrictions.minValue !== undefined &&
						value.compare(dateRestrictions.minValue) < 0
					) {
						return;
					}

					if (
						dateRestrictions.maxValue !== undefined &&
						value.compare(dateRestrictions.maxValue) > 0
					) {
						return;
					}

					setPreviewDate(value);
				}}
				value={previewDate}
			/>
		);
	}

	if (type === QuestionType.File) {
		return (
			<div className="rounded-lg border border-gray-200 border-dashed bg-gray-50 p-4 text-center">
				<p className="text-gray-500 text-sm">
					A captura de foto será enviada como resposta.
				</p>

				<p className="text-gray-400 text-xs">
					Tamanho Máximo: {settings?.maxFileSizeMB ?? 10} MB por foto.
				</p>
			</div>
		);
	}

	if (type === QuestionType.Option || type === 999) {
		const effectiveDisplayType =
			displayType ?? QuestionOptionDisplayType.Checkbox;
		const isMultipleSelection = Boolean(settings?.isMultipleSelection);

		if (
			effectiveDisplayType === QuestionOptionDisplayType.Icon ||
			type === 999
		) {
			const selectedIds = (() => {
				if (Array.isArray(selectedValue)) {
					return new Set(selectedValue);
				}
				if (selectedValue) {
					return new Set([selectedValue]);
				}
				return new Set<string>();
			})();

			return (
				<IconOptionsPreview
					onSelect={handleIconSelect}
					options={options}
					selectedIds={selectedIds}
				/>
			);
		}

		const isCheckbox =
			effectiveDisplayType === QuestionOptionDisplayType.Checkbox;
		const fieldValue = toPreviewFieldValue(isCheckbox, selectedValue);

		return (
			<OptionDisplayField
				allowDeselect={!isRequired}
				ariaLabel={title}
				isMultipleSelection={isMultipleSelection}
				onChange={(next) => {
					if (isCheckbox || isMultipleSelection) {
						if (Array.isArray(next)) {
							setSelectedValue(next);
							return;
						}
						if (next === undefined) {
							setSelectedValue([]);
							return;
						}
						setSelectedValue([next]);
						return;
					}
					setSelectedValue(next);
				}}
				optionDisplayType={effectiveDisplayType}
				options={options.map((option) => ({
					id: option.id,
					label: option.text,
				}))}
				value={fieldValue}
			/>
		);
	}

	return null;
}
